<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import BaixarRelatorio from './components/BaixarRelatorio.vue';
import AtalhoAnalitico from './components/AtalhoAnalitico.vue';
import FiltrosRelatorio from './components/FiltrosRelatorio.vue';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import BotImpactAPI from 'dashboard/api/fandata/botImpact';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import DeltaPeriodo from './DeltaPeriodo.vue';
import AbasRelatorio from './components/AbasRelatorio.vue';
import BotReports from './BotReports.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Impacto da IA (R2.14): o que o bot atendeu/resolveu/transbordou, por causa raiz.
//
// T-150g: o relatório NATIVO do bot virou a segunda aba daqui. Ele estava no menu como página
// própria, zerado, medindo o mesmo assunto que este mede melhor. Em vez de apagar, ficou dentro:
// é o número cru do Chatwoot ao lado da nossa leitura, e quem quiser conferir um contra o outro
// não precisa trocar de tela.
const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const ABA_IMPACTO = 'impacto';
const ABA_NATIVO = 'nativo';
// A rota antiga /reports/bot redireciona para cá com ?aba=nativo.
const abaAtiva = ref(route.query.aba === ABA_NATIVO ? ABA_NATIVO : ABA_IMPACTO);
const abas = computed(() => [
  { key: ABA_IMPACTO, label: t('IA_IMPACT.ABAS.IMPACTO') },
  { key: ABA_NATIVO, label: t('IA_IMPACT.ABAS.NATIVO') },
]);

const customDateRange = ref([subDays(new Date(), 29), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_30_DAYS);
const sinceTs = ref(getUnixStartOfDay(customDateRange.value[0]));
const untilTs = ref(getUnixEndOfDay(customDateRange.value[1]));
// T-252: canal e lado. Nulo e 'todos' são o padrão: quem abre a tela vê a operação inteira.
const canalFiltrado = ref(null);

const data = reactive({ counters: {}, por_assunto: [] });

// T-169/T-171: o número sozinho não deixa corrigir nada. Aqui ele vira porta de entrada para a
// lista de atendimentos, que é como a gerente acha o padrão e vai ao ticket.
//
// Os dois motivos ficam SEPARADOS de propósito: fallback é o Bira não ter conseguido responder
// (falta conteúdo ou calibração) e divergência é a base não cobrir o contrato daquele torcedor
// (o Bira acertou ao não responder). Somar os dois esconderia os dois.
const casos = reactive({
  motivo: null,
  lista: [],
  total: 0,
  carregando: false,
});

// T-206: o card do indicador abre a lista e leva os olhos até ela. Diferente do botão logo
// acima da lista, que alterna: clicar no número para FECHAR a lista lá embaixo, fora da vista,
// pareceria que o clique não fez nada.
const listaDeCasos = ref(null);

const abrirCasos = async motivo => {
  if (casos.motivo !== motivo) await verCasos(motivo);
  await nextTick();
  listaDeCasos.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const verCasos = async motivo => {
  if (casos.motivo === motivo) {
    casos.motivo = null;
    return;
  }
  casos.motivo = motivo;
  casos.carregando = true;
  try {
    const { data: resposta } = await BotImpactAPI.casos({
      since: sinceTs.value,
      until: untilTs.value,
      motivo,
    });
    casos.lista = resposta.casos || [];
    casos.total = resposta.total || 0;
  } catch (error) {
    casos.lista = [];
    casos.total = 0;
  } finally {
    casos.carregando = false;
  }
};

// Ela pediu "um relatório para ir olhando": copiar joga a lista inteira para a planilha dela.
// T-169: o ticket da lista abre a conversa em uma NOVA ABA.
//
// Sem isto a lista era um beco: o supervisor lia "TKT-401, não respondeu sobre pontos", e tinha
// de sair do relatório, procurar o número na caixa e voltar. A cada caso. O mesmo caminho que o
// painel de Jornada já abriu, e pela mesma razão: quem está analisando um período inteiro não
// pode perder o relatório a cada conversa que quiser inspecionar.
//
// É um link de verdade, e não um clique que chama window.open: assim o botão do meio, o clique
// com Ctrl e o "copiar endereço" funcionam como a pessoa espera de um link.
//
// `caso.ticket` é o display_id da conversa, que é o que a rota do painel usa.
// Duas listas trazem texto longo embaixo do caso: a da equipe traz o que foi relatado, e a do
// Bira resolvendo sozinho traz o que ele respondeu. Mesma caixa, rótulo e campo diferentes.
const TEXTO_POR_MOTIVO = {
  relato: {
    campo: 'relato',
    rotulo: 'IA_IMPACT.CASOS.RELATO_TEXTO',
    vazio: 'IA_IMPACT.CASOS.RELATO_VAZIO',
  },
  sem_transbordo: {
    campo: 'resposta',
    rotulo: 'IA_IMPACT.CASOS.RESPOSTA_TEXTO',
    vazio: 'IA_IMPACT.CASOS.RESPOSTA_VAZIA',
  },
};

const temTexto = computed(() => !!TEXTO_POR_MOTIVO[casos.motivo]);
const rotuloDoTexto = computed(
  () => TEXTO_POR_MOTIVO[casos.motivo]?.rotulo ?? ''
);
const textoDoCaso = caso => {
  const regra = TEXTO_POR_MOTIVO[casos.motivo];
  if (!regra) return '';
  return caso[regra.campo] || t(regra.vazio);
};

// Data curta na lista: quem analisa precisa saber se o caso é de ontem ou do mês passado, e
// abrir o ticket só para descobrir isso é o que torna a lista cansativa de ler.
const dataCurta = quando => {
  if (!quando) return '';
  return new Date(quando).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
};

const urlDaConversa = ticket =>
  frontendURL(
    conversationUrl({ accountId: route.params.accountId, id: ticket })
  );

const copiarCasos = () => {
  const linhas = [
    ['ticket', 'quando', 'canal', 'assunto', 'status', 'pergunta'].join('\t'),
    ...casos.lista.map(c =>
      [c.ticket, c.quando, c.canal, c.assunto, c.status, c.pergunta].join('\t')
    ),
  ];
  if (navigator.clipboard) navigator.clipboard.writeText(linhas.join('\n'));
};

// R3.11: cada card carrega a comparação com o período anterior e a DIREÇÃO em que ela é boa.
// Transbordo subindo é má notícia, e sem dizer isso o painel pintaria de verde.
const counters = computed(() => {
  const d = data.counters.delta || {};
  return [
    {
      key: 'ATENDIDAS',
      value: data.counters.atendidas ?? 0,
      accent: 'text-n-blue-11',
      delta: d.atendidas,
      sufixo: '%',
    },
    {
      key: 'RESOLVIDAS',
      value: data.counters.resolvidas ?? 0,
      accent: 'text-n-teal-11',
    },
    {
      key: 'TRANSBORDADAS',
      value: data.counters.transbordadas ?? 0,
      accent: 'text-n-ruby-11',
      delta: d.transbordadas,
      sufixo: '%',
      melhorQuando: 'desce',
    },
    {
      key: 'TAXA',
      value: `${data.counters.taxa_autoatendimento ?? 0}%`,
      accent: 'text-n-amber-11',
      delta: d.taxa_autoatendimento,
      sufixo: 'pts',
    },
    // T-150f: fallback não é todo transbordo. Passar adiante o que é de humano é o Bira
    // funcionando; fallback é ele não ter conseguido responder.
    {
      key: 'FALLBACK',
      value: `${data.counters.taxa_fallback ?? 0}%`,
      accent: 'text-n-ruby-11',
      delta: d.taxa_fallback,
      sufixo: 'pts',
      melhorQuando: 'desce',
      // T-206: um percentual sozinho não dá o que fazer. A lista dá, e é ela que vira a fila de
      // correção da base. O número passa a ser a porta para ela.
      motivo: 'fallback',
    },
  ];
});

const fetchData = async () => {
  try {
    const { data: res } = await BotImpactAPI.get({
      since: sinceTs.value,
      until: untilTs.value,
      inboxId: canalFiltrado.value,
    });
    data.counters = res.counters || {};
    data.por_assunto = res.por_assunto || [];
  } catch (e) {
    useAlert(t('IA_IMPACT.ERROR'));
  }
};

// T-190c · A PLANILHA COMPLETA DOS CASOS DO BIRA.
//
// Pedido da Tamara no T-190: "criar um botão de extração/exportação para analisar o fallback do
// Bira · planilha de exportação completa". A lista da tela é outro uso: ali ela reconhece o padrão
// e vai ao ticket, por isso vem cortada e um motivo por vez. Aqui é UM ARQUIVO com os quatro
// motivos, a coluna `motivo` dizendo qual é qual, e a coluna `relato` com o que a equipe escreveu
// quando o Bira respondeu errado · que é a coluna mais valiosa e a que a tela não mostra.
//
// CSV, e não JSON: ela disse planilha, e o destino é abrir no Excel ou jogar numa análise.
const COLUNAS = [
  'motivo',
  'ticket',
  'quando',
  'canal',
  'contato',
  'assunto',
  'status',
  'pergunta',
  'relato',
];

const celula = valor => {
  if (valor === null || valor === undefined) return '';
  // Aspas dobradas e o campo entre aspas: pergunta de torcedor tem vírgula, quebra de linha e
  // aspas, e qualquer um dos três parte a planilha em colunas erradas.
  return `"${String(valor).replace(/"/g, '""')}"`;
};

const baixandoPlanilha = ref(false);

const baixarPlanilha = async () => {
  baixandoPlanilha.value = true;
  try {
    const { data: res } = await BotImpactAPI.casosPlanilha({
      since: sinceTs.value,
      until: untilTs.value,
    });
    const linhas = res.casos || [];
    if (!linhas.length) {
      useAlert(t('IA_IMPACT.CASOS.PLANILHA_VAZIA'));
      return;
    }
    const corpo = linhas.map(l => COLUNAS.map(c => celula(l[c])).join(','));
    const csv = [COLUNAS.map(c => t(`IA_IMPACT.CASOS.COL_${c.toUpperCase()}`)).join(','), ...corpo];
    const carimbo = new Date().toISOString().slice(0, 10);
    downloadCsvFile(`fandata-casos-bira-${carimbo}.csv`, csv.join('\n'));
    if (res.cortado) useAlert(t('IA_IMPACT.CASOS.PLANILHA_CORTADA', { total: res.total }));
  } catch (e) {
    useAlert(t('IA_IMPACT.ERROR'));
  } finally {
    baixandoPlanilha.value = false;
  }
};

const onDateRangeChange = value => {
  const [start, end, rangeType] = value;
  customDateRange.value = [start, end];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  sinceTs.value = getUnixStartOfDay(start);
  untilTs.value = getUnixEndOfDay(end);
  fetchData();
};

const drillToAssunto = row => {
  router.push({
    name: 'label_conversations',
    params: { accountId: route.params.accountId, label: row.label },
  });
};

const { startRefetching } = useLiveRefresh(fetchData, 60000);
onMounted(() => {
  fetchData();
  startRefetching();
});
</script>

<template>
  <div class="flex flex-col w-full h-full px-6 py-4 overflow-auto">
    <!-- Header -->
    <div class="flex flex-col gap-3 mb-4">
      <div class="flex items-center gap-2">
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('IA_IMPACT.HEADER') }}
        </h1>
      </div>
      <p class="text-sm text-n-slate-11 -mt-1">{{ t('IA_IMPACT.HINT') }}</p>
    </div>

    <AbasRelatorio v-model="abaAtiva" :abas="abas" />

    <BotReports v-if="abaAtiva === 'nativo'" embutido />

    <template v-else>
      <div class="flex items-start mb-4 flex-wrap gap-3">
        <WootDatePicker
          v-model:date-range="customDateRange"
          v-model:range-type="selectedDateRange"
          @date-range-changed="onDateRangeChange"
        />
        <!-- T-260: baixa exatamente o que esta tela está mostrando. -->
        <!-- T-252: canal e lado. Trocar qualquer um recarrega a tela. -->
        <FiltrosRelatorio
          v-model:inbox-id="canalFiltrado"
          :com-lado="false"
          @update:inbox-id="fetchData"
        />
        <BaixarRelatorio
          nome="atendimento-ia"
          :dados="data"
          :since="sinceTs"
          :until="untilTs"
        />
        <!-- T-190c: a planilha dos casos. Botão separado do "Baixar dados" de propósito: aquele
             baixa o que a TELA mostra, e esta é a lista completa dos quatro motivos, que a tela
             não mostra junta. -->
        <NextButton
          sm
          slate
          faded
          icon="i-ph-table"
          :label="t('IA_IMPACT.CASOS.PLANILHA')"
          :title="t('IA_IMPACT.CASOS.PLANILHA_DICA')"
          :is-loading="baixandoPlanilha"
          @click="baixarPlanilha"
        />
        <!-- T-260 (leitura A): o caminho para a quebra completa, que já existe numa tela só. -->
        <AtalhoAnalitico />
      </div>

      <!-- Contadores -->
      <div class="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <component
          :is="c.motivo ? 'button' : 'div'"
          v-for="c in counters"
          :key="c.key"
          :type="c.motivo ? 'button' : null"
          class="flex flex-col gap-1 p-4 text-left rounded-xl border border-n-weak bg-n-solid-1"
          :class="
            c.motivo
              ? 'cursor-pointer transition-colors hover:border-n-brand'
              : ''
          "
          @click="c.motivo && abrirCasos(c.motivo)"
        >
          <span class="text-xs font-medium text-n-slate-11">
            {{ t(`IA_IMPACT.COUNTERS.${c.key}`) }}
            <span v-if="c.motivo" class="text-n-brand">
              {{ t('IA_IMPACT.CASOS.VER') }}
            </span>
          </span>
          <div class="flex flex-wrap gap-2 items-baseline">
            <span class="text-2xl font-semibold" :class="c.accent">
              {{ c.value }}
            </span>
            <DeltaPeriodo
              :valor="c.delta ?? null"
              :sufixo="c.sufixo || 'pts'"
              :melhor-quando="c.melhorQuando || 'sobe'"
            />
          </div>
        </component>
      </div>

      <!-- Por assunto -->
      <div class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden">
        <div class="px-5 py-3 border-b border-n-weak">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('IA_IMPACT.BY_SUBJECT') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{ t('IA_IMPACT.BY_SUBJECT_HINT') }}
          </p>
        </div>

        <div v-if="!data.por_assunto.length" class="px-5 py-10 text-center">
          <p class="text-sm text-n-slate-11">{{ t('IA_IMPACT.EMPTY') }}</p>
        </div>

        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-n-slate-11">
              <th class="text-left font-medium px-5 py-2">
                {{ t('IA_IMPACT.ASSUNTO') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('IA_IMPACT.COL_ATENDIDAS') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('IA_IMPACT.COL_RESOLVIDAS') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('IA_IMPACT.COL_TRANSBORDADAS') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('IA_IMPACT.COL_TAXA') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in data.por_assunto"
              :key="row.assunto"
              class="border-t border-n-weak hover:bg-n-alpha-1 cursor-pointer"
              @click="drillToAssunto(row)"
            >
              <td class="px-5 py-2 font-medium text-n-slate-12 capitalize">
                {{ row.label }}
              </td>
              <td class="px-3 py-2 text-center text-n-slate-12">
                {{ row.atendidas }}
              </td>
              <td class="px-3 py-2 text-center text-n-teal-11">
                {{ row.resolvidas }}
              </td>
              <td class="px-3 py-2 text-center text-n-ruby-11">
                {{ row.transbordadas }}
              </td>
              <td class="px-3 py-2 text-center font-semibold text-n-slate-12">
                {{ row.taxa }}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- T-169/T-171: os atendimentos por trás do número. Dois botões, dois problemas
           diferentes, nunca somados. -->
      <div
        ref="listaDeCasos"
        class="mt-6 rounded-xl border border-n-weak bg-n-solid-1 px-5 py-4"
      >
        <div class="flex flex-wrap items-center gap-3">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('IA_IMPACT.CASOS.TITULO') }}
          </span>
          <!-- Primeiro da fila de propósito (12/10): os outros quatro listam PROBLEMA, e quem
               abre o bloco precisa poder ler também o atendimento que deu certo. Era o pedido
               do clube: acompanhar o Bira mesmo sem transbordo. -->
          <button
            type="button"
            class="px-3 py-1 text-sm rounded-lg border transition-colors"
            :class="
              casos.motivo === 'sem_transbordo'
                ? 'border-n-brand text-n-brand'
                : 'border-n-weak text-n-slate-11'
            "
            @click="verCasos('sem_transbordo')"
          >
            {{ t('IA_IMPACT.CASOS.SEM_TRANSBORDO') }}
          </button>
          <button
            type="button"
            class="px-3 py-1 text-sm rounded-lg border transition-colors"
            :class="
              casos.motivo === 'fallback'
                ? 'border-n-brand text-n-brand'
                : 'border-n-weak text-n-slate-11'
            "
            @click="verCasos('fallback')"
          >
            {{ t('IA_IMPACT.CASOS.FALLBACK') }}
          </button>
          <button
            type="button"
            class="px-3 py-1 text-sm rounded-lg border transition-colors"
            :class="
              casos.motivo === 'card'
                ? 'border-n-brand text-n-brand'
                : 'border-n-weak text-n-slate-11'
            "
            @click="verCasos('card')"
          >
            {{ t('IA_IMPACT.CASOS.POR_CARD') }}
          </button>
          <button
            type="button"
            class="px-3 py-1 text-sm rounded-lg border transition-colors"
            :class="
              casos.motivo === 'divergencia'
                ? 'border-n-brand text-n-brand'
                : 'border-n-weak text-n-slate-11'
            "
            @click="verCasos('divergencia')"
          >
            {{ t('IA_IMPACT.CASOS.DIVERGENCIA') }}
          </button>
          <button
            type="button"
            class="px-3 py-1 text-sm rounded-lg border transition-colors"
            :class="
              casos.motivo === 'avaliacao'
                ? 'border-n-brand text-n-brand'
                : 'border-n-weak text-n-slate-11'
            "
            @click="verCasos('avaliacao')"
          >
            {{ t('IA_IMPACT.CASOS.AVALIACAO') }}
          </button>
          <!-- T-190: a lista dos erros relatados pela equipe. O construtor já sabia filtrar por
               isto desde o começo; faltava a porta na tela, e sem ela o relato ficava só dentro
               da conversa onde foi escrito. É a pergunta que a área de CS faz ao ver o recurso:
               "para onde vai o que eu escrevo?". Vai para cá. -->
          <button
            type="button"
            class="px-3 py-1 text-sm border rounded-lg"
            :class="
              casos.motivo === 'relato'
                ? 'border-n-brand text-n-brand'
                : 'border-n-weak text-n-slate-11'
            "
            @click="verCasos('relato')"
          >
            {{ t('IA_IMPACT.CASOS.RELATO') }}
          </button>
          <button
            v-if="casos.lista.length"
            type="button"
            class="ml-auto text-sm text-n-brand"
            @click="copiarCasos"
          >
            {{ t('IA_IMPACT.CASOS.COPIAR') }}
          </button>
        </div>

        <p class="mt-2 text-xs text-n-slate-11">
          {{
            t(
              `IA_IMPACT.CASOS.EXPLICACAO_${(casos.motivo || 'fallback').toUpperCase()}`
            )
          }}
        </p>

        <p v-if="casos.carregando" class="mt-3 text-sm text-n-slate-11">
          {{ t('IA_IMPACT.CASOS.CARREGANDO') }}
        </p>
        <p
          v-else-if="casos.motivo && !casos.lista.length"
          class="mt-3 text-sm text-n-slate-11"
        >
          {{ t('IA_IMPACT.CASOS.VAZIO') }}
        </p>
        <table v-else-if="casos.motivo" class="w-full mt-3 text-sm">
          <thead>
            <tr class="text-left text-xs text-n-slate-11">
              <th class="py-1 pr-3">{{ t('IA_IMPACT.CASOS.TICKET') }}</th>
              <th class="py-1 pr-3">{{ t('IA_IMPACT.CASOS.QUANDO') }}</th>
              <th class="py-1 pr-3">{{ t('IA_IMPACT.CASOS.CANAL') }}</th>
              <th class="py-1 pr-3">{{ t('IA_IMPACT.CASOS.ASSUNTO') }}</th>
              <th class="py-1">{{ t('IA_IMPACT.CASOS.PERGUNTA') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="caso in casos.lista" :key="caso.ticket">
            <tr class="border-t border-n-weak hover:bg-n-alpha-1">
              <td class="py-2 pr-3 whitespace-nowrap">
                <a
                  :href="urlDaConversa(caso.ticket)"
                  :title="t('IA_IMPACT.CASOS.ABRIR')"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="underline text-n-slate-12 hover:text-n-brand"
                >
                  {{ caso.ticket }}
                </a>
              </td>
              <td class="py-2 pr-3 text-n-slate-11 whitespace-nowrap tabular-nums">
                {{ dataCurta(caso.quando) }}
              </td>
              <td class="py-2 pr-3 text-n-slate-11 whitespace-nowrap">
                {{ caso.canal }}
              </td>
              <td class="py-2 pr-3 text-n-slate-11 capitalize">
                {{ caso.assunto || '—' }}
              </td>
              <td class="py-2 text-n-slate-11">{{ caso.pergunta }}</td>
            </tr>
            <!-- O QUE A EQUIPE ESCREVEU depois do #bira. É a informação que a pessoa que analisa
                 precisa ler para decidir se abre o caso, e até 12/10 ela só existia na planilha:
                 na tela era preciso abrir uma conversa por vez para descobrir se aquele caso
                 interessava. Vai em linha própria, e não em coluna, porque é texto de gente e não
                 cabe em célula estreita. -->
            <tr v-if="temTexto">
              <td colspan="5" class="pb-3 -mt-1 align-top">
                <div
                  class="px-3 py-2 text-sm border-l-2 rounded-r-lg border-n-brand bg-n-alpha-1 text-n-slate-12"
                >
                  <span class="text-xs uppercase tracking-wide text-n-slate-11">
                    {{ t(rotuloDoTexto) }}
                  </span>
                  <p class="mt-1 mb-0 whitespace-pre-line">
                    {{ textoDoCaso(caso) }}
                  </p>
                </div>
              </td>
            </tr>
            </template>
          </tbody>
        </table>
        <p
          v-if="casos.total > casos.lista.length"
          class="mt-2 text-xs text-n-slate-11"
        >
          {{
            t('IA_IMPACT.CASOS.CORTE', {
              mostrando: casos.lista.length,
              total: casos.total,
            })
          }}
        </p>
      </div>
    </template>
  </div>
</template>
